/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { MySitesSiteType } from "~/features/mySites/types";

export const ITEM_PER_PAGE = 15;
export const TOTAL_VISIBLE_PAGES = 5;

export const NOT_YET_APPROVED_SITE_ID = 5081;

export const SKIN_REVIEW_STATUS_DRAFT = 0;
export const SKIN_REVIEW_STATUS_PENDING = 1;
export const SKIN_REVIEW_STATUS_APPROVED = 2;
export const SKIN_REVIEW_STATUS_REJECTED = 3;
export const SKIN_REVIEW_STATUS_NEEDS_WHOIS = 4;

export enum SkinReviewStatusName {
  DRAFT = "draft",
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  NEEDS_WHOIS = "needwhois",
  UNKNOWN = "unknown",
}

export const SkinReviewStatusNameMap: Record<number, SkinReviewStatusName> = {
  [SKIN_REVIEW_STATUS_DRAFT]: SkinReviewStatusName.DRAFT,
  [SKIN_REVIEW_STATUS_PENDING]: SkinReviewStatusName.PENDING,
  [SKIN_REVIEW_STATUS_APPROVED]: SkinReviewStatusName.APPROVED,
  [SKIN_REVIEW_STATUS_REJECTED]: SkinReviewStatusName.REJECTED,
  [SKIN_REVIEW_STATUS_NEEDS_WHOIS]: SkinReviewStatusName.PENDING,
};

export const SITE_PROMOTION_STATUS_PENDING = 0;
export const SITE_PROMOTION_STATUS_ACTIVE = 1;
export const SITE_PROMOTION_STATUS_DISABLED = 2;

export enum SitePromotionStatusName {
  PENDING = "pending",
  ACTIVE = "active",
  DISABLED = "disabled",
  UNKNOWN = "unknown",
}

export const SitePromotionStatusNameMap: Record<number, SitePromotionStatusName> = {
  [SITE_PROMOTION_STATUS_PENDING]: SitePromotionStatusName.PENDING,
  [SITE_PROMOTION_STATUS_ACTIVE]: SitePromotionStatusName.ACTIVE,
  [SITE_PROMOTION_STATUS_DISABLED]: SitePromotionStatusName.DISABLED,
};

export const PAYOUT_PROGRAM_UNKNOWN = 0;
export const PAYOUT_PROGRAM_35LRS = 1;
export const PAYOUT_PROGRAM_75PS30LRS = 26;
export const PAYOUT_PROGRAM_30PJ30LRS = 21;
export const PAYOUT_PROGRAM_100PS = 16;
export const PAYOUT_PROGRAM_40PJ = 11;

export const VALID_PAYOUT_PROGRAMS = [
  PAYOUT_PROGRAM_35LRS,
  PAYOUT_PROGRAM_75PS30LRS,
  PAYOUT_PROGRAM_30PJ30LRS,
  PAYOUT_PROGRAM_100PS,
  PAYOUT_PROGRAM_40PJ,
];

export enum PayoutProgramName {
  "35LRS" = "35lrs",
  "75PS30LRS" = "75ps30lrs",
  "30PJ30LRS" = "30pj30lrs",
  "100PS" = "100ps",
  "40PJ" = "40pj",
  UNKNOWN = "unknown",
}

export const PayoutProgramNameMap: Record<number, PayoutProgramName> = {
  [PAYOUT_PROGRAM_35LRS]: PayoutProgramName["35LRS"],
  [PAYOUT_PROGRAM_75PS30LRS]: PayoutProgramName["75PS30LRS"],
  [PAYOUT_PROGRAM_30PJ30LRS]: PayoutProgramName["30PJ30LRS"],
  [PAYOUT_PROGRAM_100PS]: PayoutProgramName["100PS"],
  [PAYOUT_PROGRAM_40PJ]: PayoutProgramName["40PJ"],
};

export enum MySiteError {
  KYC = "payment info not approved",
}

export enum SiteReviewStatus {
  APPROVED = "Approved",
  PENDING = "Pending Review",
  REJECTED = "Rejected",
  DRAFT = "Draft",
  ACTIVE = "Active",
  DISABLED = "Disabled",
}

//create new site constants
export enum WizardAction {
  DOMAIN = "register_domain",
  SUBDOMAIN = "register_subdomain",
  CREATE = "createsite",
}

export enum SubdomainEnding {
  ALL = "solidcams.com",
  MALE = "streamen.com",
  TRANS = "tsmate.com",
}

export const SubdomainEndingMap: { [key: string]: SubdomainEnding } = {
  ".solidcams.com": SubdomainEnding.ALL,
  ".streamen.com": SubdomainEnding.MALE,
  ".tsmate.com": SubdomainEnding.TRANS,
};

export enum JoinType {
  PREMIUM = 1,
  FREEMIUM = 2,
}

export enum PostbackSiteType {
  WHITELABEL = "whitelabel",
  SITE_PROMOTION = "site_promotion",
}

export enum PostbackMethodType {
  GET = "get",
  POST = "post",
}

export enum PostbackEventType {
  JOIN = "join",
  TRANSACTION = "transaction",
  UPGRADE = "upgrade",
  PPS_THRESHOLD = "pps_threshold",
  VIDEO_SALE = "video_sale",
}

export const PostbackRequestParams: { [eventType in PostbackEventType]: string[] } = {
  [PostbackEventType.JOIN]: [
    "siteUrl",
    "clickId",
    "trackingId",
    "country",
    "eventType",
    "createdAt",
    "userHash",
    "joinType",
  ],
  [PostbackEventType.TRANSACTION]: [
    "siteUrl",
    "clickId",
    "trackingId",
    "amount",
    "currency",
    "spendingType",
    "eventType",
    "createdAt",
    "userHash",
  ],
  [PostbackEventType.UPGRADE]: ["siteUrl", "clickId", "trackingId", "country", "eventType", "createdAt", "userHash"],
  [PostbackEventType.PPS_THRESHOLD]: ["siteUrl", "clickId", "trackingId", "eventType", "createdAt", "userHash"],
  [PostbackEventType.VIDEO_SALE]: ["siteUrl", "clickId", "trackingId", "eventType", "createdAt", "userHash"],
};

export enum BlackLabelProduct {
  PRO = "pro",
  LITE = "lite",
  BLOCKS = "blocks",
  API = "api",
}

export const BLACKLABEL_SITE_REVIEW_STATUS_INITIAL = 0;
export const BLACKLABEL_SITE_REVIEW_STATUS_ACTIVE = 1;
export const BLACKLABEL_SITE_REVIEW_STATUS_REJECTED = 2;

export enum BlackLabelSiteReviewStatusName {
  INITIAL = "initial",
  ACTIVE = "active",
  REJECTED = "rejected",
}
export const BlackLabelSiteReviewStatusMap: Record<number, BlackLabelSiteReviewStatusName> = {
  [BLACKLABEL_SITE_REVIEW_STATUS_INITIAL]: BlackLabelSiteReviewStatusName.INITIAL,
  [BLACKLABEL_SITE_REVIEW_STATUS_ACTIVE]: BlackLabelSiteReviewStatusName.ACTIVE,
  [BLACKLABEL_SITE_REVIEW_STATUS_REJECTED]: BlackLabelSiteReviewStatusName.REJECTED,
};

export enum MySitesSiteTypeName {
  whiteLabel = "White Label",
  blackLabel = "Black Label",
  sitePromotion = "Streamate",
}

export const MySitesSiteTypeNameMap: Record<MySitesSiteType, MySitesSiteTypeName> = {
  [MySitesSiteType.whiteLabel]: MySitesSiteTypeName.whiteLabel,
  [MySitesSiteType.blackLabel]: MySitesSiteTypeName.blackLabel,
  [MySitesSiteType.sitePromotion]: MySitesSiteTypeName.sitePromotion,
};
